import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MeterViewResponse } from '@app/shared/models/meter-data/meter-view';
import { ApiService } from '@app/shared/services/api/api.service';
import { MeterFilterService } from '@app/shared/services/meter-filter.service';
import { TranslateService } from '@ngx-translate/core';
import { assign, isArray, isEmpty, isNil } from 'lodash';
import { from, Observable } from 'rxjs';
import { groupBy, map, mergeMap, toArray } from 'rxjs/operators';
import { BuildingAlarmResponse, EgAlarm, MeterBuildingAlarm } from '../models/alarms';
import { ViewBuildingAlarmsExtendedInfo1Dto, ViewBuildingAlarmsExtendedInfo1DtoWithCountTrue } from '../models/ViewBuildingAlarmsExtendedInfo1';
import { ViewMeterAlarmsExtendedInfo1DtoWithCountTrue } from '../models/ViewMeterAlarmsExtendedInfo1';

@Injectable({
  providedIn: 'root',
})
export class AlarmsService extends ApiService {
  constructor(
    public http: HttpClient,
    private readonly meterFilterService: MeterFilterService,
    private readonly translate: TranslateService,
  ) {
    super(http);
  }

  public getAlarmEventsCountByMunicipalityMeter(key: string, meterId: string): Observable<EgAlarm[]> {
    const isActiveOnlyFilter = `$filter=isActive eq true`;
    return this.http.get<EgAlarm[]>(this.endpoint(`socrates/energyguard/api/municipalities/${key}/meters/${meterId}/alarms?${isActiveOnlyFilter}`));
  }

  public getLicensedMetersCount(municipalityKey: string): Observable<number> {
    let q = '';
    q += `$filter=${this.meterFilterService.setMunicipalityFilter(municipalityKey)}`;

    const komkeyfilter = this.meterFilterService.setKomKeyFilters(false, municipalityKey);
    if (komkeyfilter.length > 0) {
      q += ` and ${komkeyfilter}`;
    }

    const isActiveOnlyFilter = this.meterFilterService.setActiveOnlyFilter();
    if (isActiveOnlyFilter.length > 0) {
      q += ` and ${isActiveOnlyFilter}`;
    }

    const energyGuardOnlyLicensedFilter = this.meterFilterService.setEnergyGuardOnlyLicensedFilter();
    if (energyGuardOnlyLicensedFilter.length > 0) {
      q += ` and ${energyGuardOnlyLicensedFilter}`;
    }

    q += `&$top=1`; // because only count is needed
    q += `&$count=true`;

    return this.http
      .get<MeterViewResponse>(this.endpoint(`socrates/meters/api/views/ViewMetersExtendedInfo1?${q}`))
      .pipe(map((res: MeterViewResponse): number => res.maxRowCount));
  }

  public getoDataLicensedMetersCount(municipalityKey: string, licenseCheck = false): Observable<BuildingAlarmResponse> {
    let q = '1=1';
    q += `&$filter=${this.meterFilterService.setMunicipalityFilter(municipalityKey)}`;

    const komkeyfilter = this.meterFilterService.setKomKeyFilters(false, municipalityKey);
    if (komkeyfilter.length > 0) {
      q += ` and ${komkeyfilter}`;
    }

    const isActiveOnlyFilter = this.meterFilterService.setActiveOnlyFilter();
    if (isActiveOnlyFilter.length > 0) {
      q += ` and ${isActiveOnlyFilter}`;
    }

    if (licenseCheck) {
      const energyGuardOnlyLicensedFilter = this.meterFilterService.setEnergyGuardOnlyLicensedFilter();
      if (energyGuardOnlyLicensedFilter.length > 0) {
        q += ` and ${energyGuardOnlyLicensedFilter}`;
      }
    }

    q += `&$expand=EnergyType($select=Name),MeterType($select=Code),Building($select=guid,isEnergyGuardNotificationActive;$expand=BuildingType($select=name)),EgAlarms($select=isActive;$expand=EgAlarmEvents;$filter=EgAlarmEvents/any())`;
    q += `&$count=true`;

    return this.http
      .get<BuildingAlarmResponse>(this.endpoint(`socrates/meters/api/municipalities/${municipalityKey}/meters?${q}`))
      .pipe(map((buildingAlarmResponse: BuildingAlarmResponse): BuildingAlarmResponse => this.mapBuildingAlarm(buildingAlarmResponse)));
  }

  public groupMeterAlarmsList(alarms: MeterBuildingAlarm[]): MeterBuildingAlarm[][] {
    const ungroupedData = from(alarms);
    const g = ungroupedData.pipe(
      groupBy((row: MeterBuildingAlarm) => row.alarmsCount! > 0),
      mergeMap((group) => group.pipe(toArray())),
    );

    const groupedData: MeterBuildingAlarm[][] = [];

    g.subscribe((val: MeterBuildingAlarm[]) => groupedData.push(val));

    return groupedData;
  }

  public getBuildingsAlarmByMunicipality(municipalityKey: string): Observable<ViewBuildingAlarmsExtendedInfo1Dto[]> {
    let url = this.endpoint(`socrates/meters/api/views/ViewBuildingAlarmsExtendedInfo1?$count=true&$filter=municipalityKey eq '${municipalityKey}'`);

    return this.http
      .get<ViewBuildingAlarmsExtendedInfo1DtoWithCountTrue>(url)
      .pipe(map((response: ViewBuildingAlarmsExtendedInfo1DtoWithCountTrue): ViewBuildingAlarmsExtendedInfo1Dto[] => response.data));
  }

  public getMetersAlarms(
    municipalityKey: string,
    connectionObjectNumber: string = '',
    meterId: string | Array<string> = '',
  ): Observable<ViewMeterAlarmsExtendedInfo1DtoWithCountTrue> {
    let q = '';
    const url = this.endpoint(`socrates/meters/api/views/ViewMeterAlarmsExtendedInfo1`);

    q += `$filter=${this.meterFilterService.setMunicipalityFilter(municipalityKey)}`;

    if (!isEmpty(connectionObjectNumber)) {
      q += ` and ${this.meterFilterService.setBuildingFilter([connectionObjectNumber])}`;
    }

    if (!isEmpty(meterId)) {
      q += ` and ${this.meterFilterService.setMeterIdsFilter(!isArray(meterId) ? [meterId] : meterId)}`;
    }
    const komkeyfilter = this.meterFilterService.setKomKeyFilters(false, municipalityKey);
    if (komkeyfilter.length > 0) {
      q += ` and ${komkeyfilter}`;
    }

    const isActiveOnlyFilter = this.meterFilterService.setActiveOnlyFilter();
    if (isActiveOnlyFilter.length > 0) {
      q += ` and ${isActiveOnlyFilter}`;
    }

    q += `&$count=true`;

    return this.http.get<ViewMeterAlarmsExtendedInfo1DtoWithCountTrue>(url + `?${q}`);
  }

  private mapBuildingAlarm(buildingAlarmResponse: BuildingAlarmResponse): BuildingAlarmResponse {
    const unknownBuildingTypeText = this.translate.instant('module.energyGuard.common.unknownObjectText');
    const buildingAlarms = buildingAlarmResponse.data.map((buildingAlarm: MeterBuildingAlarm) => {
      const events = [];
      buildingAlarm.egAlarms?.forEach((egAlarm) => {
        if (egAlarm.isActive) {
          events.push(...egAlarm.egAlarmEvents);
        }
      });

      return assign(buildingAlarm, {
        buildingTypesName: (isNil(buildingAlarm.building.buildingType) ? null : buildingAlarm.building.buildingType.name) || unknownBuildingTypeText,
        alarms: buildingAlarm.egAlarms,
        alarmsCount: events.length,
        isEnergyGuardNotificationActive: buildingAlarm.building.isEnergyGuardNotificationActive,
        isEnergyGuardLicenseAvailable: buildingAlarm.isEnergyGuardLicenseAvailable,
      });
    });
    return { data: buildingAlarms, maxRowCount: buildingAlarmResponse.maxRowCount };
  }
}
